import { useLocale } from "react-polyglot-hooks"
import styled from "styled-components"
import LazyLoad from "react-lazyload"
import * as PropTypes from "prop-types"
import { sendEvent } from "scripts/GoogleAnalytics"
import Typography from "components/styled/Typography"
import Col from "components/styled/Grid/Col"
import { Container, Row } from "components/styled/Grid"
import Link from "components/Link"
import { SecondaryButton } from "components/styled/Buttons/SecondaryButton"
import YoutubeVideo from "components/YoutubeVideo"
import { mainVideoId } from "helpers/constants"

const CarImage = styled.img`
  min-width: 240px;
  max-width: 240px;
  margin-left: 5px;
`

const TopImage = styled.img`
  margin-bottom: 50px;
  min-height: 230px;
  object-fit: cover;
  width: 100%;
  @media ${({ theme }) => theme.breakpoints.lg} {
    margin: 0;
    height: 100%;
  }
`

const ImageCol = styled(Col)`
  padding: 0;
  @media ${({ theme }) => theme.breakpoints.xl} {
    padding: 0;
  }
`

const Column = styled(Col)`
  @media ${({ theme }) => theme.breakpoints.md} {
    padding: 0;
  }
  @media ${({ theme }) => theme.breakpoints.lg} {
    padding: 0 25px;
  }
`

const Item = styled.div`
  margin-bottom: 25px;
`

const Wrapper = styled(Container)`
  margin-top: -25px;
  @media ${({ theme }) => theme.breakpoints.md} {
    margin-top: 25px;
  }
  @media ${({ theme }) => theme.breakpoints.lg} {
    max-width: none;
  }
  @media ${({ theme }) => theme.breakpoints.xl} {
    max-width: 1230px;
  }
`

const StyledRow = styled(Row)`
  margin: 0 -25px;
  @media ${({ theme }) => theme.breakpoints.md} {
    margin: 0;
  }
  @media ${({ theme }) => theme.breakpoints.lg} {
    margin: 0 25px;
  }
`

const Title = styled(Typography).attrs({
  el: "h3",
  variant: "title4",
  weight: "medium"
})`
  margin-bottom: 15px;
`
const Description = styled(Typography).attrs({
  variant: "small",
  weight: "light",
  color: "skyBlue"
})``

const HeadTitle = styled(Typography)`
  margin: 25px 0;
`

const Youtube = styled(YoutubeVideo)`
  margin-bottom: 75px;
  padding: 0;
`

export default function AboutRenting({ landing }) {
  const locale = useLocale()

  const onPlayVideo = () => {
    sendEvent("Video", { event_category: landing, event_label: "play" })
  }

  return (
    <Wrapper top={0} bottom={50}>
      <HeadTitle variant="title2" weight="medium" space center>
        Swipcar en televisión
      </HeadTitle>
      <StyledRow>
        <ImageCol xs={12} lg={6}>
          <LazyLoad height={150}>
            {mainVideoId[locale] ? <Youtube youtubeId={mainVideoId[locale]} onPlay={onPlayVideo} /> : <HeaderImage />}
          </LazyLoad>
        </ImageCol>
        <Column xs={7} md={8} lg={4} className="pr-0">
          <Item>
            <Title>Sin entrada</Title>
            <Description>No tendrás que hacer una aportación inicial.</Description>
          </Item>
          <Item>
            <Title>Todo incluido</Title>
            <Description>Seguro, mantenimiento, impuestos, revisiones… ¡todo!</Description>
          </Item>
          <Item>
            <Title>Una cuota mensual</Title>
            <Description>Precios sin letra pequeña.</Description>
          </Item>
          <Item>
            <Title>100% online</Title>
            <Description>Reserva tu coche nuevo desde el móvil.</Description>
          </Item>
          <Button className="d-none d-md-block" page={landing} />
        </Column>
        <Column xs={5} md={4} lg={2} className="pl-0">
          <LazyLoad height={200}>
            <CarImage
              alt="Renting Swipcar"
              src={`${process.env.NEXT_PUBLIC_CLOUDINARY}w_auto,q_auto,f_auto/static/car_top`}
            />
          </LazyLoad>
        </Column>
      </StyledRow>
      <Button className="d-block d-md-none" page={landing} />
    </Wrapper>
  )
}

AboutRenting.propTypes = {
  landing: PropTypes.string.isRequired
}

const StyledButton = styled(SecondaryButton)`
  margin-top: 50px;
  @media ${({ theme }) => theme.breakpoints.md} {
    margin: 25px 0 0;
  }
`

function Button(props) {
  const { page, ...rest } = props

  const handleClick = () => {
    sendEvent("Click content", { event_category: page, event_label: "How it work" })
  }

  return (
    <Link page="about">
      <StyledButton size="lg" border {...rest} onClick={handleClick}>
        ¿Cómo funciona?
      </StyledButton>
    </Link>
  )
}

Button.propTypes = {
  page: PropTypes.string.isRequired
}

function HeaderImage() {
  const backgroundUrl = `${process.env.NEXT_PUBLIC_CLOUDINARY}w_auto,f_auto,q_auto/static/home`

  return (
    <picture>
      <source
        srcSet={`${backgroundUrl}/movie-375px,
                ${backgroundUrl}/movie-375px_2x 2x,
                ${backgroundUrl}/movie-375px_3x 3x`}
        media="(max-width: 576px)"
      />
      <source
        srcSet={`${backgroundUrl}/movie-768px,
                ${backgroundUrl}/movie-768px_2x 2x,
                ${backgroundUrl}/movie-768px_3x 3x`}
        media="(max-width: 992px)"
      />
      <source
        srcSet={`${backgroundUrl}/movie-1024px,
                ${backgroundUrl}/movie-1024px_2x 2x,
                ${backgroundUrl}/movie-1024px_3x 3x`}
        media="(max-width: 1024px)"
      />
      <source
        srcSet={`${backgroundUrl}/movie-1440px,
                ${backgroundUrl}/movie-1440px_2x 2x,
                ${backgroundUrl}/movie-1440px_3x 3x`}
      />
      <TopImage alt="About renting" src={`${backgroundUrl}/movie-768px`} />
    </picture>
  )
}
